// @react-compiler enable
import {getSessionStatusIcon} from '@github-ui/agent-sessions/components/StatusIcon'
import {SessionState} from '@github-ui/agent-sessions/types/session'
import {generateTaskPathForRepo} from '@github-ui/agent-sessions/utils/generate-task-path'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {testIdProps} from '@github-ui/test-id-props'
import {CopilotIcon} from '@primer/octicons-react'
import {Link, RelativeTime} from '@primer/react'
import {memo, useCallback, useEffect, useMemo, useRef} from 'react'

import type {AgentInfo, AgentTask} from '../../api/memex-items/contracts'
import {useAgentTaskSidePanelEnabled} from '../../features/copilot-tasks/use-agent-task-sidepanel-enabled'
import {useTableCopilotTasks} from '../../features/copilot-tasks/use-table-copilot-tasks'
import {isCopilot} from '../../helpers/assignee-helper'
import {useSidePanel} from '../../hooks/use-side-panel'
import type {MemexItemModel} from '../../models/memex-item-model'
import {handleKeyboardNavigation, suppressEvents} from '../../navigation/keyboard'
import {RemoveSessionMenu} from '../fields/agent-task-popover'
import styles from './agent-task-sub-row.module.css'
import {shouldShowAgentTaskPill} from './agent-task-utils'
import {focusAgentTaskRow, TableFocusType, useStableTableNavigation, useTableNavigation} from './navigation'

interface AgentTaskSubRowProps {
  model: MemexItemModel
  /** Individual task data to render. */
  task: AgentTask
  /** Callback invoked after the server confirms the assignment was removed. */
  onRemoveAssignment?: (assignmentId: number) => void
  /** TanStack row ID of the parent row, used for table navigation focus tracking. */
  rowId?: string
  /** TanStack row ID of this agent task sub-row, used as a unique navigation identity. */
  taskRowId?: string
  /** Hierarchy depth of the parent row (0 for top-level items). */
  depth?: number
  /** Whether the table hierarchy (Show hierarchy toggle) is enabled. */
  hierarchyEnabled?: boolean
  /** Total width (px) of visible columns before the Title column, excluding the drag handle. */
  titleColumnOffset?: number
  /** Whether this is the last sub-row in the group (controls connector line style). */
  isLast?: boolean
  /** Number of visible columns in the table, used for aria-colspan on the gridcell. */
  columnCount?: number
}

function AgentTaskAgentIcon({agent}: {agent?: AgentInfo}) {
  const isCopilotAgent = agent ? isCopilot(agent.login) : false
  const hasAvatar = !!agent?.avatarUrl
  const altText = agent?.displayName || agent?.login

  if (agent && !isCopilotAgent && hasAvatar && altText) {
    return <GitHubAvatar src={agent.avatarUrl} alt={altText} size={16} />
  }

  return <CopilotIcon size={16} />
}

export const AgentTaskSubRow = memo(function AgentTaskSubRow({
  model,
  task,
  onRemoveAssignment,
  rowId,
  taskRowId,
  depth = 0,
  hierarchyEnabled = false,
  titleColumnOffset = 0,
  isLast = true,
  columnCount = 1,
}: AgentTaskSubRowProps) {
  const {copilotTasksEnabled} = useTableCopilotTasks()
  const sidePanelEnabled = useAgentTaskSidePanelEnabled()
  const rowRef = useRef<HTMLDivElement>(null)
  const cellRef = useRef<HTMLDivElement>(null)
  const linkRef = useRef<HTMLAnchorElement>(null)
  const {state} = useTableNavigation()
  const {navigationDispatch} = useStableTableNavigation()
  const {openPaneAgentTask} = useSidePanel()

  const taskId = task.id
  const repoId = model.ownerId()
  const taskUrl = useMemo(() => {
    if (!taskId) return undefined
    const repositoryNwo = task.repoNwo ?? (model.getNameWithOwner()?.join('/') || undefined)
    return generateTaskPathForRepo({taskId, repositoryNwo}) || undefined
  }, [taskId, task.repoNwo, model])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!sidePanelEnabled) return
      // Allow cmd/ctrl+click and middle-click to open in new tab
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.metaKey || e.ctrlKey || e.button === 1) return
      if (taskId && repoId != null) {
        e.preventDefault()
        openPaneAgentTask(taskId, repoId)
      }
    },
    [sidePanelEnabled, taskId, repoId, openPaneAgentTask],
  )

  const isFocused =
    state.focus?.type === TableFocusType.AGENT_TASK_ROW &&
    state.focus.details?.parentRowId === rowId &&
    state.focus.details?.taskRowId === taskRowId

  // Focus the gridcell element when table navigation state targets this agent task row
  useEffect(() => {
    if (!isFocused) return

    const cell = cellRef.current
    if (!cell) return

    // Avoid stealing focus from interactive descendants (e.g., the task link)
    if (typeof document !== 'undefined') {
      const active = document.activeElement
      if (active instanceof Element && cell.contains(active)) {
        return
      }
    }

    cell.focus()
  }, [isFocused])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Enter or Space activates the task link only when the gridcell itself has focus
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if ((e.key === 'Enter' || e.key === ' ') && linkRef.current && e.target === cellRef.current) {
        e.preventDefault()
        linkRef.current.click()
        return
      }
      const result = handleKeyboardNavigation(navigationDispatch, e)
      if (result.action) {
        suppressEvents(e)
      }
    },
    [navigationDispatch],
  )

  const onFocus = useCallback(
    (e: React.FocusEvent) => {
      // Only handle focus events on the gridcell itself, not bubbled from children
      if (e.target === cellRef.current && rowId && taskRowId) {
        navigationDispatch(focusAgentTaskRow(rowId, taskRowId))
      }
    },
    [navigationDispatch, rowId, taskRowId],
  )

  if (!copilotTasksEnabled) return null
  if (!shouldShowAgentTaskPill(task.state)) return null
  const isQueued = task.state === SessionState.Queued
  const statusIcon = getSessionStatusIcon(task.state)

  return (
    <div
      ref={rowRef}
      role="row"
      className={styles.AgentTaskSubRow}
      style={
        {
          '--agent-task-depth': depth,
          '--hierarchy-enabled': hierarchyEnabled ? 1 : 0,
          '--title-column-offset': titleColumnOffset,
        } as React.CSSProperties
      }
      {...testIdProps('agent-task-sub-row')}
    >
      <div
        ref={cellRef}
        role="gridcell"
        aria-colspan={columnCount}
        tabIndex={0}
        className={styles.AgentTaskGridCell}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
      >
        <span
          className={`${styles.connector} ${isLast ? styles.connectorLast : styles.connectorMiddle}`}
          aria-hidden="true"
        />
        <div className={styles.contentWrapper}>
          <span className={styles.statusIcon}>{statusIcon}</span>
          <span className={styles.agentIcon}>
            <AgentTaskAgentIcon agent={task.agent} />
          </span>
          {task.title &&
            (taskUrl ? (
              <Link
                ref={linkRef}
                href={taskUrl}
                className={styles.title}
                onClick={handleClick}
                onFocus={() => rowId && taskRowId && navigationDispatch(focusAgentTaskRow(rowId, taskRowId))}
              >
                {task.title}
              </Link>
            ) : (
              <span className={styles.titleMuted}>{task.title}</span>
            ))}
          {!isQueued && task.lastUpdatedAt && (
            <span className={styles.elapsedTime}>
              Updated <RelativeTime date={new Date(task.lastUpdatedAt)} />
            </span>
          )}
          {task.assignmentId != null && task.sourceRepoNwo && (
            <RemoveSessionMenu
              assignmentId={task.assignmentId}
              sourceRepoNwo={task.sourceRepoNwo}
              onRemoveAssignment={onRemoveAssignment}
              className={styles.kebabButton}
              taskTitle={task.title}
            />
          )}
        </div>
      </div>
    </div>
  )
})
